import type { ReactNode } from 'react';
import { IdControl } from '../components/IdControl';
import { JsonViewerButton } from '../components/JsonViewer';
import { CollapsibleCodeBlock } from './CollapsibleCodeBlock';
import { ErrorBanner } from './ErrorBanner';

/** Minimal shape of a run error record — code/stage/provider chips. */
export interface RunErrorRecordLike {
    code: string;
    stage?: string;
    provider?: string;
    providerCode?: string;
    message?: string;
}

function accessoryRow(...nodes: (ReactNode | null | false | undefined)[]): ReactNode {
    const visible = nodes.filter(Boolean) as ReactNode[];
    if (visible.length === 0) return undefined;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {visible.map((n, i) => <span key={i} style={{ display: 'inline-flex' }}>{n}</span>)}
        </span>
    );
}

export function ErrorRecordChips({ records }: { records: RunErrorRecordLike[] }) {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0' }}>
            {records.map((rec, i) => (
                <span
                    key={i}
                    title={rec.message}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '2px 8px', borderRadius: 10, fontSize: 11,
                        border: '1px solid rgba(239, 68, 68, 0.35)', background: 'rgba(239, 68, 68, 0.10)',
                    }}
                >
                    <span style={{ fontWeight: 700 }}>{rec.code}</span>
                    {rec.stage && <span style={{ color: 'var(--text-secondary)' }}>{rec.stage}</span>}
                    {rec.provider && <span style={{ color: 'var(--text-secondary)' }}>{rec.provider}</span>}
                    {rec.providerCode && <span style={{ color: 'var(--text-secondary)' }}>{rec.providerCode}</span>}
                </span>
            ))}
        </div>
    );
}

/**
 * Shared "Model Output" block — the run's error banner, structured error chips,
 * quality-check badges, and the Reasoning Summary / Full LLM Output / Raw
 * Response / Parsed collapsibles. Used by BOTH the standalone Model Run detail
 * page and the operation Submission/Hint run-detail card so the two stay
 * identical. `qualityBadges` and `parsed` are page-specific slots (each page
 * builds its own schema-aware parsed body and quality-badge component).
 *
 * See docs/FeatureDesigns/operation-detail-unified-layout.md.
 */
export interface ModelOutputViewProps {
    /** Precomputed error banner text (e.g. "[max_tokens] …"). */
    error?: string | null;
    /** Structured error records → code/stage/provider chips below the banner. */
    errorRecords?: RunErrorRecordLike[];
    /** Quality-check badges slot (page-specific component). */
    qualityBadges?: ReactNode;
    /** Reasoning summary text (Responses API reasoning models). */
    reasoningSummary?: string | null;
    /** Full LLM output incl. reasoning items (source of truth on new rows). */
    rawOutputFull?: string | null;
    /** Visible/derived raw response. */
    rawResponse?: string | null;
    /** ModelOutput id — IdControl next to the raw-response section. */
    modelOutputId?: string | null;
    /** Schema-aware parsed body (page-specific). Wrapped in the parsed collapsible. */
    parsed?: ReactNode;
    /** Title for the parsed collapsible. Defaults to "Parsed Output". */
    parsedTitle?: string;
    /** Whether the parsed collapsible starts expanded. Defaults to true. */
    parsedDefaultExpanded?: boolean;
}

export function ModelOutputView({
    error,
    errorRecords,
    qualityBadges,
    reasoningSummary,
    rawOutputFull,
    rawResponse,
    modelOutputId,
    parsed,
    parsedTitle = 'Parsed Output',
    parsedDefaultExpanded = true,
}: ModelOutputViewProps) {
    const rawResponseLabel = rawOutputFull ? 'Raw LLM Response (Visible Answer)' : 'Raw LLM Response';
    return (
        <>
            {error && <ErrorBanner>{error}</ErrorBanner>}
            {errorRecords && errorRecords.length > 0 && <ErrorRecordChips records={errorRecords} />}
            {qualityBadges}
            {reasoningSummary && (
                <CollapsibleCodeBlock
                    title="Reasoning Summary"
                    text={reasoningSummary}
                    titleAccessory={accessoryRow(
                        <JsonViewerButton text={reasoningSummary} title="Reasoning Summary" />,
                    )}
                />
            )}
            {rawOutputFull && (
                <CollapsibleCodeBlock
                    title="Full LLM Output (incl. reasoning items)"
                    text={rawOutputFull}
                    titleAccessory={accessoryRow(
                        <JsonViewerButton text={rawOutputFull} title="Full LLM Output" />,
                    )}
                />
            )}
            <CollapsibleCodeBlock
                title={rawResponseLabel}
                text={rawResponse}
                titleAccessory={accessoryRow(
                    <JsonViewerButton text={rawResponse} title={rawResponseLabel} />,
                    modelOutputId ? <IdControl value={modelOutputId} container="modelOutputs" /> : null,
                )}
            />
            {parsed !== undefined && (
                <CollapsibleCodeBlock title={parsedTitle} defaultCollapsed={!parsedDefaultExpanded}>
                    {parsed}
                </CollapsibleCodeBlock>
            )}
        </>
    );
}
